// Which ranks the replacement prize table pays, and which it leaves empty —
// t-159, the coverage strip of
// .codelane/artifacts/t-159/leaderboard-pending-rewards.html (984-995).
//
// Its own module rather than a helper inside the tab, for the reason written
// at the top of tabs/cycleRange.ts: a file that exports both components and
// plain functions cannot be hot-replaced, so arithmetic lives beside the
// component, never in it.
//
// A warning, never a refusal. A board is allowed to pay nothing to a band of
// ranks — plenty do it on purpose — so this counts the empty bands and says
// where they are, and the scheduling button stays exactly as it was.

import type { RankBasis, RewardSpec } from "./types";

export interface CoverageBand {
  /** Both bounds inclusive, as ranks are everywhere else on this page. */
  from: number;
  to: number;
  covered: boolean;
}

export interface RankCoverage {
  /**
   * Whether a bar can be drawn at all. A table that ranks by percentile has no
   * fixed band of ranks — 1% of a cycle is a different set of players every
   * week — so there is nothing to draw and the caller says so in one line
   * instead.
   */
  drawable: boolean;
  /** The bar, left to right: solid where a prize lands, hatched where none
     does. Empty when nothing is drawable. */
  bands: CoverageBand[];
  /** Just the empty ones, in the order they appear — what the warning names. */
  gaps: CoverageBand[];
  /** The boundaries printed under the bar: where it starts, then the end of
     every band. */
  scale: number[];
  /** How many rows rank by percentile. Non-zero is the whole reason a table
     may not be drawable. */
  percentileRows: number;
  /** The last rank any row of the table reaches. */
  lastRank: number;
}

const EMPTY: RankCoverage = {
  drawable: false,
  bands: [],
  gaps: [],
  scale: [],
  percentileRows: 0,
  lastRank: 0,
};

function isNum(v: number | undefined | null): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** The rank band a row pays, or nothing at all. A tier threshold has none —
   it is a rung of the ladder, not a slice of the ranking — and neither has a
   percentile row, which is counted separately. */
function rankBandOf(r: RewardSpec): { from: number; to: number } | null {
  if (!isNum(r.rank_from) || !isNum(r.rank_to)) return null;
  const from = Math.max(1, Math.floor(r.rank_from));
  const to = Math.floor(r.rank_to);
  if (to < from) return null;
  return { from, to };
}

/**
 * The table read as one strip of ranks.
 *
 * Overlapping rows are merged rather than reported: two prizes on the same
 * rank is a question about the prize table, not about coverage, and the strip
 * only ever answers "is this rank paid for".
 */
export function rankCoverage(rows: readonly RewardSpec[]): RankCoverage {
  const percentileRows = rows.filter(
    (r) => isNum(r.percentile_from) || isNum(r.percentile_to),
  ).length;

  const spans = rows
    .map(rankBandOf)
    .filter((b): b is { from: number; to: number } => b !== null)
    .sort((a, b) => a.from - b.from || a.to - b.to);

  if (spans.length === 0) return { ...EMPTY, percentileRows };

  const merged: { from: number; to: number }[] = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    // `<= last.to + 1` and not `<= last.to`: 1–10 followed by 11–20 leaves no
    // rank unpaid, and drawing a hairline gap between them would be a warning
    // about nothing.
    if (last && span.from <= last.to + 1) {
      last.to = Math.max(last.to, span.to);
      continue;
    }
    merged.push({ ...span });
  }

  const bands: CoverageBand[] = [];
  // The strip starts at rank 1 even when the table does not: a board whose
  // first prize is for rank 5 is not paying ranks 1-4, and that is exactly the
  // kind of table this strip exists to show.
  let cursor = 1;
  for (const span of merged) {
    if (span.from > cursor) {
      bands.push({ from: cursor, to: span.from - 1, covered: false });
    }
    bands.push({ from: span.from, to: span.to, covered: true });
    cursor = span.to + 1;
  }

  const lastRank = merged[merged.length - 1].to;
  return {
    // A percentile row can reach any rank, so no rank band can be called
    // empty while one is in the table.
    drawable: percentileRows === 0,
    bands,
    gaps: bands.filter((b) => !b.covered),
    scale: [bands[0].from, ...bands.map((b) => b.to)],
    percentileRows,
    lastRank,
  };
}

/** How wide a band is drawn — its share of the strip, in ranks. */
export function bandWeight(band: CoverageBand): number {
  return band.to - band.from + 1;
}

/** "Ranks 51 – 100" / "Rank 51" — the subject of the warning sentence. */
export function bandLabel(band: CoverageBand): string {
  return band.from === band.to
    ? `Rank ${band.from}`
    : `Ranks ${band.from} – ${band.to}`;
}

// --- One strip per ranking --------------------------------------------------
//
// A rank is only ever a place inside ONE ranking, and a board can hold several
// at once: every tier of a tier board restarts at rank 1 (MaterializeGrants
// partitions by tier_key), and a member_contribution board carries two whole
// tables side by side (rank_basis — the team's place in its room, and the
// member's place inside their own team).
//
// Read as one strip they fold into each other and the warning stops working in
// both directions: 1–10 in gold and 1–10 in silver cover each other, so a
// league paying nothing above rank 3 looks complete — and an entity table
// ending at 3 beside a member table starting at 50 invents a 4–49 gap that
// exists in neither. `rewardIdentity` has always read a row as (basis, tier,
// range); this is the same rule applied to coverage.

export interface CoverageGroup {
  /** The ranking this strip is about, as a stable key. */
  key: string;
  /** The heading above the strip — absent when the table holds a single
     ranking and a heading would name the only thing on the screen. */
  label?: string;
  coverage: RankCoverage;
}

/** Absent means `entity`, the substitution the server makes for every reward
   written before the column existed. */
function basisOf(r: RewardSpec): RankBasis {
  return r.rank_basis === "guild_member" ? "guild_member" : "entity";
}

function uniq(keys: readonly string[]): string[] {
  return [...new Set(keys)];
}

/**
 * The table split into the rankings it actually describes, each with its own
 * strip.
 *
 * `boardTiers` is the board's configured ladder, and it is worth passing:
 * a table whose only tier'd rows are for `gold` still leaves `silver` and
 * `bronze` to be judged, and a hole there is exactly the kind this warning
 * exists for. Tier keys the table names but the board does not are kept as
 * well — an orphaned row still pays nothing to nobody, and hiding it would
 * hide the reason.
 */
export function rankCoverageGroups(
  rows: readonly RewardSpec[],
  boardTiers: readonly string[] = [],
): CoverageGroup[] {
  const bases: RankBasis[] = ["entity", "guild_member"];
  const present = bases.filter((b) => rows.some((r) => basisOf(r) === b));
  // An empty table still answers, with the one group it would have had — the
  // caller says "nothing to draw" in a single place rather than two.
  const used = present.length > 0 ? present : (["entity"] as RankBasis[]);

  const groups: { key: string; basis: RankBasis; tier?: string; rows: RewardSpec[] }[] =
    [];
  for (const basis of used) {
    const ofBasis = rows.filter((r) => basisOf(r) === basis);
    // A `guild_member` row is never tier'd (see RankBasis), so in practice
    // this list is empty on that half and the board keeps its one strip.
    const tiers = uniq([
      ...boardTiers,
      ...ofBasis.map((r) => r.tier_key).filter((k): k is string => !!k),
    ]);
    if (tiers.length === 0) {
      groups.push({ key: basis, basis, rows: ofBasis });
      continue;
    }
    for (const tier of tiers) {
      groups.push({
        key: `${basis}|${tier}`,
        basis,
        tier,
        // A row with NO tier_key pays inside every tier — the grant SQL
        // matches `r.tier_key IS NULL OR r.tier_key = ranked.tier_key` — so it
        // joins each group rather than forming one of its own. Leaving it out
        // would report a hole every unbound prize already fills.
        rows: ofBasis.filter((r) => !r.tier_key || r.tier_key === tier),
      });
    }
  }

  // A heading only where there is something to tell apart. The basis half is
  // named only on a board that has both — on every other one "entity" is not a
  // distinction, it is the board.
  const named = groups.length > 1;
  const multiBasis = used.length > 1;
  return groups.map((g) => {
    const parts: string[] = [];
    if (g.tier) parts.push(`tier ${g.tier}`);
    if (multiBasis) parts.push(`basis ${g.basis}`);
    return {
      key: g.key,
      label: named && parts.length > 0 ? parts.join(" · ") : undefined,
      coverage: rankCoverage(g.rows),
    };
  });
}
